import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cpu, MapPin } from "lucide-react";
import { api } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { SERIAL_STATUS } from "@/components/warehouse/SerialsTab";
import { MOVE_TYPES } from "@/components/warehouse/MovementsTab";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export const useSerialHits = (q, enabled) => {
  const [hits, setHits] = useState([]);
  useEffect(() => {
    const c = q.trim();
    if (!enabled || c.length < 5) { setHits([]); return undefined; }
    const t = setTimeout(() => api.get("/warehouse/serials", { params: { q: c } }).then((r) => setHits(r.data.slice(0, 8))).catch(() => setHits([])), 300);
    return () => clearTimeout(t);
  }, [q, enabled]);
  return hits;
};

const Position = ({ s }) => {
  if (s.status === "INSTALLED") return <>Installato presso cliente{s.external_order_id && <> · pratica <Link className="font-mono font-semibold underline" to={`/ordini/${s.work_order_id}`}>{s.external_order_id}</Link></>}{s.installed_at && ` · ${fmtDateTime(s.installed_at)}`}</>;
  if (s.warehouse_name) return <>{s.warehouse_name}</>;
  return <>{s.last_warehouse_name ? `Ultimo magazzino: ${s.last_warehouse_name}` : "Fuori magazzino"}</>;
};

export const SerialHistoryDialog = ({ serialId, onClose }) => {
  const [d, setD] = useState(null);
  useEffect(() => {
    setD(null);
    if (serialId) api.get(`/warehouse/serials/${serialId}/history`).then((r) => setD(r.data)).catch(() => onClose());
  }, [serialId, onClose]);
  const s = d?.serial;
  const [label, cls] = (s && SERIAL_STATUS[s.status]) || [s?.status, ""];
  return (
    <Dialog open={!!serialId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto" data-testid="serial-history-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2 font-display text-2xl"><Cpu className="h-5 w-5" /><span className="font-mono text-lg" data-testid="serial-history-serial">{s?.serial || "…"}</span></DialogTitle></DialogHeader>
        {s && (
          <div className="space-y-4">
            <div className="grid gap-3 border bg-muted/30 p-4 text-sm sm:grid-cols-2">
              <div><div className="micro-label">Apparato</div><span className="font-mono text-xs font-semibold">{s.item_code}</span> · {s.item_name}</div>
              <div><div className="micro-label">Committente</div>{s.customer_name || "—"}</div>
              <div><div className="micro-label">Stato</div><span className={`px-2 py-0.5 text-xs font-semibold ${cls}`} data-testid="serial-history-status">{label}</span></div>
              <div><div className="micro-label">Posizione</div><span className="flex items-start gap-1" data-testid="serial-history-position"><MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0" /><span><Position s={s} /></span></span></div>
            </div>
            <div>
              <div className="micro-label mb-2">Storia movimenti ({d.movements.length})</div>
              <ol className="space-y-0 border-l-2 pl-4" data-testid="serial-history-moves">
                {d.movements.map((m) => (
                  <li key={m.id} className="relative pb-3 text-sm" data-testid={`serial-history-move-${m.id}`}>
                    <span className="absolute -left-[21px] top-1.5 h-2.5 w-2.5 rounded-full bg-foreground" />
                    <div className="flex flex-wrap justify-between gap-2"><b>{MOVE_TYPES[m.type] || m.type}</b><span className="font-mono text-xs text-muted-foreground">{fmtDateTime(m.at)} · {m.by}</span></div>
                    <div className="text-xs">{m.from_name || "Fornitore / committente"} → {m.to_name || (m.type === "CONSUME" ? "Cliente" : "Uscita")}
                      {m.external_order_id && <> · <Link className="font-mono font-semibold underline" to={`/ordini/${m.work_order_id}`}>{m.external_order_id}</Link></>}</div>
                    {m.notes && <div className="text-xs text-muted-foreground">{m.notes}</div>}
                  </li>
                ))}
                {!d.movements.length && <li className="text-sm text-muted-foreground">Nessun movimento registrato.</li>}
              </ol>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
